import { type ChangeEvent, useCallback, useId, useMemo } from 'react'

import { colors } from '@lib/theme'

import InputField from './input-field/with-managed-state'

interface RangeSliderProps {
  value: number
  setValue: (newValue: number) => void
  min: number
  max: number
  step?: number
  label?: string
  onChange?: (value: number) => void
}

const accentColor = colors.green
const backgroundColor = 'rgba(12,12,12,0.15)'

const RangeSlider = ({
  value,
  setValue,
  min,
  max,
  step = 0.1,
  label,
  onChange,
}: RangeSliderProps) => {
  const id = useId()

  const progressPercent = useMemo(() => {
    const range = max - min
    return ((value - min) / range) * 100
  }, [value, min, max])

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = Number(event.target.value)
      setValue(newValue)

      if (onChange) {
        onChange(newValue)
      }
    },
    [onChange, setValue],
  )

  return (
    <div className="flex items-center gap-4">
      {!!label && (
        <label className="shrink-0" htmlFor={id}>
          {label}
        </label>
      )}

      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleChange}
        style={{
          background: `linear-gradient(to right, ${accentColor} 0%, ${accentColor} ${progressPercent}%, ${backgroundColor} ${progressPercent}%, ${backgroundColor} 100%)`,
        }}
      />

      <InputField
        id={id}
        type="number"
        min={min}
        max={max}
        step={step}
        value={value.toString()}
        setValue={(value) => setValue(Number(value))}
        onBlur={handleChange}
        inputClassName="text-center min-w-[60px]"
      />
    </div>
  )
}

export default RangeSlider
